/**
 * Recipe application logic for the ProEpoxy Metallic calculator.
 *
 * Given a curated recipe from the catalogue and the current calculator state,
 * `applyRecipeToState` returns a set of state mutations the picker performs
 * against `useMetallicOrder`. Split out into its own module so it stays
 * testable in isolation and the picker component doesn't grow into a
 * 300-line side-effect soup.
 */
import {
  EPOXY_PRIMERS,
  DESIGN_COAT_PRODUCTS,
  PIGMENTS_PRODUCTS,
  TOP_COAT_PRODUCTS,
} from './productData';
import { calculateOptimalPacks } from './utils';

/**
 * Convert a recipe's `{color, percent}[]` blend into concrete
 * `{color, size, quantity}[]` metallic-powder line items, sized against the
 * installer's total m² and the recipe's grams-per-m² density.
 *
 * Rule of thumb: we round the per-colour need UP to the next 100g pack, then
 * consolidate into 1kg packs where the count reaches 10. That way installers
 * never come up short of a colour mid-pour.
 */
export function computePigmentLineItems({ totalM2, pigmentGPerM2, pigments }) {
  const totalGrams = Math.max(0, Math.round(totalM2 * pigmentGPerM2));
  if (!totalGrams || !pigments?.length) return [];
  const items = [];
  for (const p of pigments) {
    const gramsForColour = Math.ceil((totalGrams * p.percent) / 100);
    if (gramsForColour <= 0) continue;
    // Pack sizes on Metallic Pigment Powders are 100g and 1kg. Prefer 1kg
    // once we cross 900g to save the installer a pallet of tiny tubs.
    let onekgs = Math.floor(gramsForColour / 1000);
    let hundreds = Math.ceil((gramsForColour - onekgs * 1000) / 100);
    if (hundreds >= 10) {
      onekgs += 1;
      hundreds = 0;
    }
    if (onekgs > 0) items.push({ color: p.color, size: '1kg', quantity: onekgs });
    if (hundreds > 0) items.push({ color: p.color, size: '100g', quantity: hundreds });
  }
  return items;
}

/**
 * Merge a computed pigment line-item list into the existing
 * `pigmentSelections.metallicPowders`. Replaces the whole `metallicPowders`
 * array so re-applying a recipe never leaves stale colours behind — pastes,
 * glitter and sprays are untouched.
 */
export function buildPigmentSelectionsUpdate(currentSelections, lineItems) {
  return {
    ...currentSelections,
    metallicPowders: lineItems,
  };
}

/**
 * Hydrate a single `primerAreas[]` entry from the recipe. Runs pack
 * calculators against the recipe's primer/topcoat/design coat choices, so
 * the calculator jumps straight to the ready-to-review state.
 */
export function applyRecipeToArea(area, recipe) {
  const m2 = parseInt(area.m2) || 0;
  const primer = recipe.primer || { type: '1_part', color: 'White' };
  const design = recipe.design_coat || { product_type: 'comfort' };
  const topCoat = recipe.top_coat || { product_type: 'polyurethane', finish: 'Satin' };
  const floor = recipe.floor_prep || {};

  const primerConfig = EPOXY_PRIMERS[primer.type] || EPOXY_PRIMERS['1_part'];
  const primerColor = primerConfig.colors.includes(primer.color)
    ? primer.color
    : primerConfig.colors[0];

  const topCoatConfig = TOP_COAT_PRODUCTS[topCoat.product_type] || TOP_COAT_PRODUCTS.polyurethane;
  const topCoatFinish = topCoatConfig.finishes.includes(topCoat.finish)
    ? topCoat.finish
    : topCoatConfig.finishes[0];

  return {
    ...area,
    needsSmoothingCompounds: (floor.smoothing_compound_ids || []).length > 0 ? true : area.needsSmoothingCompounds,
    smoothingCompounds: (floor.smoothing_compound_ids || []).reduce((acc, id) => ({ ...acc, [id]: Math.max(1, acc[id] || 0) }), area.smoothingCompounds || {}),
    needsSubFloorPrimer: (floor.sub_floor_primer_ids || []).length > 0 ? true : area.needsSubFloorPrimer,
    subFloorPrimers: (floor.sub_floor_primer_ids || []).reduce((acc, id) => ({ ...acc, [id]: Math.max(1, acc[id] || 0) }), area.subFloorPrimers || {}),
    needsPrimer: true,
    primerType: primer.type,
    color: primerColor,
    packs: m2 > 0 ? calculateOptimalPacks(m2, primerConfig.packs) : area.packs || [],
    // Design coat lives in `designCoatSelections` (see applyRecipeDesignCoat), so we
    // just persist the product_type intent here for downstream steps.
    designCoatType: design.product_type,
    topCoatType: topCoat.product_type,
    topCoatFinish,
    topCoatPacks: m2 > 0 ? calculateOptimalPacks(m2, topCoatConfig.packs) : [],
  };
}

/**
 * Build the `designCoatSelections` map for the recipe — Comfort resin gets
 * auto-sized packs; Crystal gets a unit count based on min coverage.
 */
export function applyRecipeDesignCoat(currentSelections, primerAreas, recipe) {
  const type = (recipe.design_coat && recipe.design_coat.product_type) || 'comfort';
  const next = { ...currentSelections };
  primerAreas.forEach((area) => {
    const m2 = parseInt(area.m2) || 0;
    if (m2 <= 0) return;
    if (type === 'comfort') {
      next[area.id] = {
        productType: 'comfort',
        packs: calculateOptimalPacks(m2, DESIGN_COAT_PRODUCTS.comfort.packs),
        crystalUnits: 0,
      };
    } else {
      next[area.id] = {
        productType: 'crystal',
        packs: [],
        crystalUnits: Math.ceil(m2 / DESIGN_COAT_PRODUCTS.crystal.coverageMin),
      };
    }
  });
  return next;
}

/**
 * Human-readable summary of what applying a recipe will change — used in the
 * toast confirmation so installers see exactly what got prefilled.
 */
export function recipeSummary(recipe) {
  const parts = [];
  if (recipe.primer) parts.push(`Primer: ${recipe.primer.type === '2_part' ? '2-part DPM' : '1-part Rapid'} · ${recipe.primer.color}`);
  if (recipe.design_coat) parts.push(`Design coat: ${recipe.design_coat.product_type === 'crystal' ? 'Crystal' : 'Comfort'}`);
  if (recipe.pigments?.length) {
    const blend = recipe.pigments.map((p) => `${p.color} ${p.percent}%`).join(' + ');
    parts.push(`Pigments: ${blend}`);
  }
  if (recipe.top_coat) parts.push(`Top coat: ${recipe.top_coat.product_type === 'polyaspartic' ? 'Polyaspartic' : 'Polyurethane'} ${recipe.top_coat.finish}`);
  return parts.join(' · ');
}

// Re-export coverage data used by the summary line — keeps consumers from
// having to know productData details.
export const PIGMENT_PRODUCTS_ROOT = PIGMENTS_PRODUCTS;
